import {createContext, useContext, useEffect, useState} from "react";
import {getAdminSessionData} from "../utils/AdminUtils";
import {checkNullArr, checkNullJson, checkNullStr, getJsonValue, getJsonValueFromNestedKeys, isBoolTrue, jsonToStr,} from "../../common/fusion-ui/utils/ReactUtils";
import {AdminKeys} from "../config/AdminConfig";
import {printError, printLog} from "../../common/CommonUtils";


const LocalStoreContext = createContext(null);
const TAG = "LocalStoreProvider";

/**
 * LocalStoreProvider component is responsible for providing localStorage
 * management within the application. It uses React's Context API to provide
 * state and functions related to local storage access and modification.
 *
 * @param {Object} props - Properties passed to the component.
 * @param {ReactNode} props.children - Child components that will have access
 * to the local storage context.
 *
 * The component initializes its state with data retrieved from localStorage
 * and provides methods to get, set, and check localStorage fields, updating
 * its state accordingly.
 *
 * @function getLocalStoreData - Retrieves and parses data from localStorage
 * using a specified key and returns it. If an error occurs or data is null,
 * it returns an empty object.
 *
 * @function updateLocalStoreField - Updates a specific field in localStorage
 * with a given value and optionally persists it. It also updates the context
 * state with the new data.
 *
 * @function getLocalStoreField - Fetches the value of a specified key from
 * the localStorage data in the context state. It supports nested key access.
 *
 * @function checkLocalStoreFieldNullJson - Checks if the value of a specific
 * localStorage field is null or JSON null.
 *
 * @function checkLocalStoreFieldNullStr - Checks if the value of a given
 * localStorage field is a null string.
 *
 * @constant {Object} value - Contains the localStorage data and the functions
 * for accessing and modifying the localStorage, which are provided to the
 * React Context.
 *
 * @returns {JSX.Element} LocalStoreContext.Provider - A Context provider component
 * that wraps its children and provides localStorage-related functionalities.
 */
function LocalStoreProvider({children}) {
    const LS_SUPER_KEY = AdminKeys.lsSessionSuperKey;
    const [localStorageData, setLocalStoreData] = useState(getLocalStoreData());

    useEffect(() => {
        setLocalStoreData(getLocalStoreData());
    }, []);

    function getLocalStoreData() {
        try {
            const data = checkNullJson(getAdminSessionData()) ? JSON.parse((localStorage.getItem(LS_SUPER_KEY))) : {};
            // const data = checkNullJson(getAdminSessionData()) ? JSON.parse(unscrambleData(localStorage.getItem(LS_SUPER_KEY))) : {};
            return checkNullJson(data) ? data : {};
        } catch (error) {
            logErr("getLocalStoreData:", error);
            return {};
        }
    }

    // Function to update local data
    function updateLocalStoreField(key, value, storePersist = false) {
        // log('updateLocalStoreField.key:', key, 'value:', value);
        try {
            if (checkNullJson(getAdminSessionData()) || isBoolTrue(storePersist)) {
                const lsData = getLocalStoreData();
                // log( 'updateLocalStoreField.lsData:', lsData, 'type:', typeof lsData);
                // lsData[key] = isBoolTrue(convertJsonToStr) ? jsonToStr(value) : value;
                lsData[key] = value;
                localStorage.setItem(LS_SUPER_KEY, (jsonToStr(lsData, 0)));
                // localStorage.setItem(LS_SUPER_KEY, scrambleData(jsonToStr(lsData, 0)));
                setLocalStoreData(lsData);
            }
        } catch (error) {
            logErr("updateLocalStoreField.error:", error);
        }
    }

    function getLocalStoreField(key, defValue) {
        // log( 'getLocalStoreField.key:', key, 'defValue:', defValue);
        // return getJsonValue(localStorageData, key, defValue);
        return checkNullArr(key) ? getJsonValueFromNestedKeys(localStorageData, key, defValue) : getJsonValue(localStorageData, key, defValue);
    }

    function checkLocalStoreFieldNullJson(field) {
        return checkNullJson(getLocalStoreField(field));
    }

    function checkLocalStoreFieldNullStr(field) {
        return checkNullStr(getLocalStoreField(field));
    }

    const value = {
        localStorageData,
        updateLocalStoreField,
        getLocalStoreField,
        checkLocalStoreFieldNullJson,
        checkLocalStoreFieldNullStr,
    };

    return <LocalStoreContext.Provider value={value}>
        {children}
    </LocalStoreContext.Provider>;
}

function log(...text) {
    printLog(TAG, ...text);
}

function logErr(...text) {
    printError(TAG, ...text);
}

/**
 * A hook to update & get local-storage data.
 * @example
 *     //import hook
 *     import {useLocalStoreData} from "../../../redux/reduxHooks";
 *
 *     //Get local-data and essential methods from useLocalStoreData.
 *     const {
 *         localData,
 *         updateLocalStoreField,
 *         getLocalStoreField
 *     } = useLocalStoreData({
 *         keys: [Fields.searchInputValue, Fields.searchInputValue,],
 *         // defValues: ['', DEF_OPTION,], //EXPERIMENTAL
 *     });
 *
 *     //print local-data on change
 *     useEffect(() => {
 *          console.log('useEffect.localData:', localData);
 *     }, [localData]);
 *
 *     //Declare necessary fields
 *      const Fields = {
 *         searchInputValue: 'inputValue',
 *      };
 *
 *      //Input to demonstrate the use of getLocalFieldValue && updateLocalFieldValue methods
 *       <input
 *          value={getLocalFieldValue(Fields.searchInputValue, DEF_OPTION)}
 *          onChange={(e) => {
 *              updateLocalFieldValue(Fields.searchInputValue, e.target.value);
 *          }}/>
 *
 *     //Function to update local-field value.
 *     function updateLocalFieldValue(field, value) {
 *         updateLocalStoreField(field, value);
 *     }
 *
 *     //Function to get local-field value.
 *     function getLocalFieldValue(field, defValue) {
 *         return getLocalStoreField(field, defValue);
 *     }
 */
function useLocalStoreData() {
    return useContext(LocalStoreContext);
}

export {LocalStoreProvider, useLocalStoreData};
